import React, { useEffect, useRef, useState } from 'react';
import { DxfViewer, ToolbarMenuId } from '@pattern-x/gemini-viewer-threejs';
import ProgressLine from '../progressLine';
import { getPresignedUrl } from '@/services/initial';
import type { EventEmitter } from 'events';
import styles from './index.module.less';
import { useCallbackFn } from '@/hooks';

type props = {
  data: ELEMENT.modelFiles;
  fileId: string;
  dwgEventBus: EventEmitter;
};

let isAbort = false;

export default React.memo((props: props) => {
  const { data, fileId, dwgEventBus } = props;
  const viewer = useRef<DxfViewer>();
  const [percent, setPercent] = useState(0);
  let isFullscreen = false;

  // dxfview对象配置项
  const config = {
    containerId: 'dwgCanvas',
    enableAxisGizmo: true,
    enableLayoutBar: true,
    enableStats: true,
    enableToolbar: true,
    enableSpinner: false,
    enableBottomBar: true,
    enableProgressBar: false,
    enableSelection: true,
    toolbarMenuConfig: {
      [ToolbarMenuId.Fullscreen]: {
        onClick: () => {
          if (document.fullscreenElement === null) {
            document.documentElement?.requestFullscreen();
            isFullscreen = true;
          } else {
            document.exitFullscreen();
            isFullscreen = false;
          }
        },
      },
      [ToolbarMenuId.ZoomToRectangle]: { visible: false },
      [ToolbarMenuId.Screenshot]: { visible: false },
      [ToolbarMenuId.Measure]: { visible: false },
      [ToolbarMenuId.Markup]: { visible: false },
      [ToolbarMenuId.MarkupVisibility]: { visible: false },
      [ToolbarMenuId.Layers]: { visible: false },
      [ToolbarMenuId.Settings]: { visible: false },
    },
  };

  // esc键盘事件，用于退出全屏状态
  const escFunc = (evt: any) => {
    if (evt.keyCode === 27) {
      isFullscreen = false;
    }
  };

  // 监听全屏状态，用于同步改变全屏按键的激活状态
  const exitHandler = () => {
    if (document.fullscreenElement !== null) {
      viewer.current?.toolbar?.menuList?.get(ToolbarMenuId.Fullscreen)?.setActive(true);
      isFullscreen = true;
    } else {
      viewer.current?.toolbar?.menuList?.get(ToolbarMenuId.Fullscreen)?.setActive(false);
      isFullscreen = false;
    }
  };

  // 图纸初始化方法
  const fn = async () => {
    viewer.current = new DxfViewer(config);
    viewer.current.showStats();
    const fontFile = ['/three/fonts/hztxt.shx', '/three/fonts/simplex.shx'];
    await viewer.current.setFont(fontFile);
    const urlRes = await getPresignedUrl({
      fileId: data?.storageFileId,
      ignoreAttachment: true,
    });
    if (urlRes?.data?.downloadUrl) {
      const models = [
        {
          name: decodeURIComponent(data.name + '.dwg'),
          modelId: data.storageFileId,
          src: urlRes?.data?.downloadUrl,
          position: [0, 0, 0],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
          visible: true,
          merge: true,
        },
      ];
      // eslint-disable-next-line @typescript-eslint/no-loop-func
      models.forEach((modelCfg) => {
        if (modelCfg.visible === false) {
          // visible is true by default
          return; // only load visible ones
        }
        const onProgress = (event: any) => {
          const per = Math.round((event.loaded * 100) / event.total);
          if (per < 100) {
            isAbort = false;
          }
          setPercent(per);
        };
        try {
          viewer.current
            ?.loadModelAsync(modelCfg, onProgress)
            ?.then(async () => {
              console.log(`[Demo] Loaded model ${modelCfg.src}`);
            })
            .finally(() => {
              document.addEventListener('keydown', escFunc);
              setPercent(100);
              viewer.current?.toolbar?.menuList
                ?.get(ToolbarMenuId.Fullscreen)
                ?.setActive(isFullscreen);
            });
        } catch (ex) {
          console.log(ex);
        }
      });
    } else {
      // TODO 模拟数据
    }
  };

  useEffect(() => {
    if (fileId) {
      fn();
    }
  }, [fileId]);

  const destroyDxf = useCallbackFn(() => {
    if (viewer.current) {
      viewer.current?.destroy?.();
      viewer.current = undefined;
      setPercent(0);
    }
    if (percent < 100) {
      isAbort = true;
    }
  });

  useEffect(() => {
    document.addEventListener('fullscreenchange', exitHandler);
    dwgEventBus.addListener('destroyDxf', destroyDxf);
    return () => {
      isAbort = false;
      if (viewer?.current) {
        viewer.current?.destroy?.();
        viewer.current = undefined;
      }

      if (document.fullscreenElement !== null) {
        document.exitFullscreen(); // 关闭页面时退出全屏模式
      }
      document.removeEventListener('fullscreenchange', exitHandler);
      dwgEventBus.removeListener('destroyDxf', destroyDxf);
    };
  }, []);

  return (
    <div id="viewerContainer" className={styles.dwgWrap}>
      <div id="dwgCanvas" className={styles.dwgContainer}>
        {(percent < 99 || isAbort) && (
          <div className={styles.dwgLoadingContainer}>
            <div className={styles.loadingTitle}>正在打开图纸</div>
            <ProgressLine percent={isAbort ? 0 : percent} width={320} />
          </div>
        )}
      </div>
    </div>
  );
});
